/**
 * The game-end review switch (SPEC §2.6).
 *
 * The end of the game is two surfaces, not one: the **results** (final
 * leaderboard + superlatives, which assert conclusions) and the **history**
 * (every completed ranking, which shows the working). Results stays the
 * default — the leaderboard is still the payoff — and the history is one tap
 * away rather than appended to the bottom of a scroll nobody reaches.
 *
 * Hidden entirely when there is no history: a game where every turn was voided
 * has nothing to review, and a tab leading to an apology is worse than no tab.
 */
import type { ReactNode } from 'react';

export type ReviewTab = 'results' | 'history';

export function ReviewTabs({
  tab,
  onChange,
  historyCount,
}: {
  tab: ReviewTab;
  onChange: (tab: ReviewTab) => void;
  historyCount: number;
}) {
  return (
    <div
      role="tablist"
      aria-label="Game review"
      data-testid="review-tabs"
      className="sticky top-0 z-10 -mx-5 mb-3 flex gap-2 bg-sand px-5 pt-1 pb-2.5"
    >
      <Tab id="results" tab={tab} onChange={onChange}>
        Results
      </Tab>
      <Tab id="history" tab={tab} onChange={onChange}>
        Every ranking <span className="tabular-nums">({historyCount})</span>
      </Tab>
    </div>
  );
}

function Tab({
  id,
  tab,
  onChange,
  children,
}: {
  id: ReviewTab;
  tab: ReviewTab;
  onChange: (tab: ReviewTab) => void;
  children: ReactNode;
}) {
  const active = tab === id;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={`review-tab-${id}`}
      onClick={() => onChange(id)}
      className={`min-h-11 flex-1 rounded-btn border px-3 text-[0.8125rem] font-semibold transition ${
        active ? 'border-terracotta bg-terracotta text-cream' : 'border-line bg-cream text-ink'
      }`}
    >
      {children}
    </button>
  );
}
